import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Label, StringSelect, Input, RichTextEditor, Checkbox, Button } from "@tellescope/ui";
import type { ImageUploadHandler } from "@tellescope/ui";
import { useUploadTellescopeFile } from "@tellescope/tanstack-query";
import { Eye } from "lucide-react";
import type { PopupBlock } from "../../../../../shared/schema";
import { DISMISSAL_PERIOD_OPTIONS } from "../../../../../shared/schema";
import { PopupDialog } from "../../../../../shared/components/PopupDialog";
import { LayoutSection } from "../shared/LayoutSection";
import { VisibilitySection } from "../shared/VisibilitySection";
import type { BlockTypeEditorProps } from "../types";
import { useAuth } from "@tellescope/tanstack-query";

type PopupBlockEditorProps = BlockTypeEditorProps<PopupBlock>;

export function PopupBlockEditor({
  block,
  updateProps,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: PopupBlockEditorProps) {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const { session } = useAuth();
  const { uploadFileAsync } = useUploadTellescopeFile({
    session,
    returnDownloadURL: true,
  });

  const uploadImage: ImageUploadHandler = useCallback(
    async (file: File) => {
      if (!session) {
        console.error("No active session for image upload");
        return null;
      }

      try {
        const result = await uploadFileAsync(
          {
            name: file.name,
            size: file.size,
            type: file.type,
            publicRead: true,
          },
          file
        );

        if (result.downloadURL) {
          return { url: result.downloadURL };
        }

        return null;
      } catch (error) {
        console.error("Failed to upload image:", error);
        return null;
      }
    },
    [session, uploadFileAsync]
  );

  const isCustomVariant = block.props.variant === "custom";
  const showPeriodSelector = block.props.dismissalBehavior === "period";

  return (
    <div className="space-y-4">
      {/* Preview Button */}
      <Button variant="outline" className="w-full" onClick={() => setShowPreview(true)}>
        <Eye className="mr-2 h-4 w-4" />
        {t("Preview Popup")}
      </Button>

      {/* Preview Dialog - uses shared component for consistency with live popup */}
      <PopupDialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        content={block.props.content}
        variant={block.props.variant}
        size={block.props.size}
        title={block.props.title}
        customBackgroundColor={block.props.customBackgroundColor}
        customTextColor={block.props.customTextColor}
      />

      <div className="space-y-2">
        <Label>{t("Title (optional)")}</Label>
        <Input
          value={block.props.title || ""}
          onChange={(e) => updateProps({ title: e.target.value || undefined })}
          placeholder={t("Popup title...")}
        />
      </div>

      <div className="space-y-2">
        <Label>{t("Popup Content")}</Label>
        <RichTextEditor
          content={block.props.content}
          onChange={(content) => updateProps({ content })}
          placeholder={t("Enter popup content...")}
          onImageUpload={uploadImage}
        />
      </div>

      <StringSelect
        label={t("Style")}
        value={block.props.variant}
        onValueChange={(value) => updateProps({ variant: value })}
        options={[
          { value: "info", label: t("Info (Blue)") },
          { value: "success", label: t("Success (Green)") },
          { value: "warning", label: t("Warning (Yellow)") },
          { value: "error", label: t("Error (Red)") },
          { value: "neutral", label: t("Neutral (Gray)") },
          { value: "custom", label: t("Custom Color") },
        ]}
      />

      {isCustomVariant && (
        <div className="space-y-4 pl-4 border-l-2 border-muted">
          <div className="space-y-2">
            <Label>{t("Background Color")}</Label>
            <Input
              type="color"
              value={block.props.customBackgroundColor || "#f0f9ff"}
              onChange={(e) => updateProps({ customBackgroundColor: e.target.value })}
              className="w-20 h-10 p-1"
            />
          </div>
          <div className="space-y-2">
            <Label>{t("Text Color")}</Label>
            <Input
              type="color"
              value={block.props.customTextColor || "#1e3a5f"}
              onChange={(e) => updateProps({ customTextColor: e.target.value })}
              className="w-20 h-10 p-1"
            />
          </div>
        </div>
      )}

      <StringSelect
        label={t("Size")}
        value={block.props.size ?? "default"}
        onValueChange={(value) => updateProps({ size: value })}
        options={[
          { value: "default", label: t("Default") },
          { value: "wide", label: t("Wide") },
        ]}
      />

      <StringSelect
        label={t("Dismissal Behavior")}
        value={block.props.dismissalBehavior ?? "always"}
        onValueChange={(value) => updateProps({ dismissalBehavior: value })}
        options={[
          { value: "always", label: t("Show every visit") },
          { value: "period", label: t("Show again after time period") },
          { value: "forever", label: t("Never show again once dismissed") },
        ]}
      />
      <p className="text-xs text-muted-foreground">
        {block.props.dismissalBehavior === "always" && t("Popup reappears each time the page is refreshed")}
        {block.props.dismissalBehavior === "period" && t("Popup reappears after the selected time period")}
        {block.props.dismissalBehavior === "forever" && t("Once dismissed, popup never shows again")}
      </p>

      {showPeriodSelector && (
        <StringSelect
          label={t("Reshow After")}
          value={block.props.dismissalPeriod ?? "1d"}
          onValueChange={(value) => updateProps({ dismissalPeriod: value })}
          options={DISMISSAL_PERIOD_OPTIONS}
        />
      )}

      <div className="flex items-center gap-2">
        <Checkbox
          id="showOnAllPages"
          checked={block.props.showOnAllPages ?? false}
          onCheckedChange={(checked) => updateProps({ showOnAllPages: !!checked })}
        />
        <label htmlFor="showOnAllPages" className="text-sm cursor-pointer">
          {t("Show on all pages")}
        </label>
      </div>
      <p className="text-xs text-muted-foreground">
        {t("When enabled, this popup appears on every page, not just this one")}
      </p>

      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}
